"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import Button from "./Button";

const Header: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeMobileSubmenu, setActiveMobileSubmenu] = useState<string | null>(
    null
  );
  const [activeDesktopSubmenu, setActiveDesktopSubmenu] = useState<
    string | null
  >(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);


  // Component yüklenme kontrolü
  useEffect(() => {
    // Component mount edildiğinde isLoaded'i true yap
    setIsLoaded(true);
  }, []);

  // Body scroll lock effect - mobile menü açıldığında
  useEffect(() => {
    if (isMobileMenuOpen) {
      // Scroll'u engelle
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.top = "0";
      document.body.style.left = "0";
      document.body.style.right = "0";
    } else {
      // Scroll'u geri aç
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
    }

    // Cleanup - component unmount edildiğinde
    return () => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
    };
  }, [isMobileMenuOpen]);

  // Scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close desktop menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      // Sadece desktop menü için dış tıklama kontrolü
      if (activeDesktopSubmenu && !target.closest(".desktop-menu-item")) {
        setActiveDesktopSubmenu(null);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [activeDesktopSubmenu]);

  // Mobile menu toggle function
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    // Submenu'yu da kapat
    setActiveMobileSubmenu(null);
  };

  // Mobile submenu toggle - mobile menü kapatılmasın
  const toggleMobileSubmenu = (menu: string) => {
    setActiveMobileSubmenu(activeMobileSubmenu === menu ? null : menu);
  };

  // Icons
  const ChevronDownIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M6 9l6 6l6 -6"></path>
    </svg>
  );

  const ChevronLeftIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="32"
      height="32"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M9 6l6 6l-6 6"></path>
    </svg>
  );

  const MenuIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="3" y1="6" x2="21" y2="6"></line>
      <line x1="3" y1="12" x2="21" y2="12"></line>
      <line x1="3" y1="18" x2="21" y2="18"></line>
    </svg>
  );

  const CloseIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="18" y1="6" x2="6" y2="18"></line>
      <line x1="6" y1="6" x2="18" y2="18"></line>
    </svg>
  );

  const BackIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M19 12H5"></path>
      <path d="M12 19l-7-7 7-7"></path>
    </svg>
  );

  const WordIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="mt-n1-4"
    >
      <path d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0"></path>
      <path d="M3.6 9h16.8"></path>
      <path d="M3.6 15h16.8"></path>
      <path d="M11.5 3a17 17 0 0 0 0 18"></path>
      <path d="M12.5 3a17 17 0 0 1 0 18"></path>
    </svg>
  );

  const toggleDesktopSubmenu = (menu: string) => {
    if (activeDesktopSubmenu === menu) {
      // Aynı menüye tıklandıysa kapat
      setActiveDesktopSubmenu(null);
    } else if (activeDesktopSubmenu) {
      // Farklı menüye tıklandıysa animasyon ile değiştir
      setIsTransitioning(true);
      setTimeout(() => {
        setActiveDesktopSubmenu(menu);
        setTimeout(() => {
          setIsTransitioning(false);
        }, 50); // Yeni içerik için kısa delay
      }, 200); // Eski içeriğin kaybolma süresi
    } else {
      // Hiç menü açık değilse direkt aç
      setActiveDesktopSubmenu(menu);
    }
  };

  // Menu content renderer
  const renderMenuContent = () => {
    if (!isLoaded) return null;

    switch (activeDesktopSubmenu) {
      case "products":
        return (
          <div className="grid grid-cols-1 gap-8">
            <div className="space-y-4 mt-[26px] ml-[11px]">
              <Link
                href="benefits-for-patients"
                className="block py-2 text-[45px] text-primary-600 hover:text-primary-500 font-light transition-colors"
              >
                Hastalar İçin Faydalar
              </Link>
              <Link
                href="/benefits-for-dentists"
                className="block py-2 text-[45px] text-primary-600 hover:text-primary-500 font-light transition-colors"
              >
                Diş Hekimleri İçin Faydalar
              </Link>
              <Link
                href="/product-lines"
                className="block py-2 text-[45px] text-primary-600 hover:text-primary-500 font-light transition-colors"
              >
                Ürün Grupları
              </Link>
              <Link
                href="/benefits-for-dentists/tissue-level"
                className="block py-2 text-[45px] text-primary-600 hover:text-primary-500 font-light transition-colors"
              >
                Bilimsel Çalışmalar
              </Link>
              <Link
                href="#"
                className="hidden py-2 text-[45px] text-primary-600 hover:text-primary-500 font-light transition-colors"
              >
                Kullanıcı
              </Link>
              <Link
                href="/sscp"
                className="block py-2 text-[45px] text-primary-600 hover:text-primary-500 font-light transition-colors"
              >
                Güvenlik ve Klinik Performans
              </Link>
            </div>
          </div>
        );
      case "service":
        return (
          <div className="grid grid-cols-1 gap-8">
            <div className="space-y-4 mt-[26px] ml-[11px]">
              <Link
                href="/service"
                className="block py-2 text-[45px] text-primary-600 hover:text-primary-500 font-light transition-colors"
              >
                Servis ve Destek
              </Link>
              <Link
                href="/documents"
                className="block py-2 text-[45px] text-primary-600 hover:text-primary-500 font-light transition-colors"
              >
                Müşteri Hizmetleri
              </Link>
              <Link
                href="/sds-podcast"
                className="block py-2 text-[45px] text-primary-600 hover:text-primary-500 font-light transition-colors"
              >
                SDS Podcast
              </Link>
              <Link
                href="/contact"
                className="block py-2 text-[45px] text-primary-600 hover:text-primary-500 font-light transition-colors"
              >
                İletişim
              </Link>
            </div>
          </div>
        );
      case "about":
        return (
          <div className="grid grid-cols-1 gap-8">
            <div className="space-y-4 mt-[26px] ml-[11px]">
              <Link
                href="/company"
                className="block py-2 text-[45px] text-primary-600 hover:text-primary-500 font-light transition-colors"
              >
               SDS Switzerland
              </Link>
              <Link
                href="/company-krea"
                className="block py-2 text-[45px] text-primary-600 hover:text-primary-500 font-light transition-colors"
              >
               SDS Türkiye Distribütörü - Krea
              </Link>
              <Link
                href="/career"
                className="hidden py-2 text-[45px] text-primary-600 hover:text-primary-500 font-light transition-colors"
              >
                Kariyer
              </Link>
              <Link
                href="/history"
                className="block py-2 text-[45px] text-primary-600 hover:text-primary-500 font-light transition-colors"
              >
               Tarih
              </Link>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  // Mobile submenu content renderer
  const renderMobileSubmenuContent = () => {
    if (!isLoaded) return null;

    switch (activeMobileSubmenu) {
      case "products":
        return (
          <div className="space-y-4">
            <Link
              href="/benefits-for-patients"
              className="block py-3 text-primary-600 hover:text-primary-500 transition-colors text-lg"
              onClick={() => toggleMobileMenu()}
            > 
             Hastalar İçin Faydalar
            </Link>
            <Link
              href="/benefits-for-dentists"
              className="block py-3 text-primary-600 hover:text-primary-500 transition-colors text-lg"
              onClick={() => toggleMobileMenu()}
            >
              Diş Hekimleri İçin Faydalar
            </Link>
            <Link
              href="/product-lines"
              className="block py-3 text-primary-600 hover:text-primary-500 transition-colors text-lg"
              onClick={() => toggleMobileMenu()}
            >
              Ürün Grupları
            </Link>
            <Link
              href="#"
              className="block py-3 text-primary-600 hover:text-primary-500 transition-colors text-lg"
              onClick={() => toggleMobileMenu()}
            >
              Bilimsel Çalışmalar
            </Link>
            <Link
              href="#"
              className="hidden py-3 text-primary-600 hover:text-primary-500 transition-colors text-lg"
              onClick={() => toggleMobileMenu()}
            >
             Kullanıcı
            </Link>
            <Link
              href="#"
              className="block py-3 text-primary-600 hover:text-primary-500 transition-colors text-lg"
              onClick={() => toggleMobileMenu()}
            >
              Güvenlik ve Klinik Performans
            </Link>
          </div>
        );
      case "service":
        return (
          <div className="space-y-4">
            <Link
              href="/service"
              className="block py-3 text-primary-600 hover:text-primary-500 transition-colors text-lg"
              onClick={() => toggleMobileMenu()}
            >
             Servis ve Destek
            </Link>
            <Link
              href="/documents"
              className="block py-3 text-primary-600 hover:text-primary-500 transition-colors text-lg"
              onClick={() => toggleMobileMenu()}
            >
              Müşteri Hizmetleri
            </Link>
            <Link
              href="/sds-podcast"
              className="block py-3 text-primary-600 hover:text-primary-500 transition-colors text-lg"
              onClick={() => toggleMobileMenu()}
            >
              Dokümantasyon
            </Link>
            <Link
              href="/contact"
              className="block py-3 text-primary-600 hover:text-primary-500 transition-colors text-lg"
              onClick={() => toggleMobileMenu()}
            >
              İletişim
            </Link>
          </div>
        );
      case "about":
        return (
          <div className="space-y-4">
              <Link
                href="/company"
                onClick={toggleMobileMenu}
                className="block py-2 text-lg text-primary-600 hover:text-primary-500 font-light transition-colors"
              >
                SDS Switzerland
              </Link>
              <Link
                href="/company-krea"
                onClick={toggleMobileMenu}
                className="block py-2 text-lg text-primary-600 hover:text-primary-500 font-light transition-colors"
              >
               SDS Türkiye Distribütörü - Krea
              </Link>
              <Link
                href="/career"
                onClick={toggleMobileMenu}
                className="hidden py-2 text-lg text-primary-600 hover:text-primary-500 font-light transition-colors"
              >
                Kariyer
              </Link>
              <Link
                href="/history"
                onClick={toggleMobileMenu}
                className="block py-2 text-lg text-primary-600 hover:text-primary-500 font-light transition-colors"
              >
               Tarih
              </Link>
            </div>
        );
      default:
        return null;
    }
  };

  const renderLanguageToggle = () => {
    if (!isLoaded) return null;
    return (
      <>
        <Link href="https://swissdentalsolutions.com.tr" className="ml-4" target="_blank" rel="noopener noreferrer">
          <Button
            variant="customOutline"
            size="custom16"
            rounded="rounded-full"
            className="!shadow-none mt-8 !font-[400] flex cursor-pointer"
            iconPosition="right"
            icon={<WordIcon />}
          >
          EN/English
        </Button>
        </Link>
      </>
    );
  };

  // Loading state - çeviriler yüklenene kadar basit header göster
  if (!isLoaded) {
    return (
      <header className="relative z-50 bg-transparent container-dental">
        <div className="header-logo h-[75px] lg:h-31 flex items-center">
          <div className="">
            <Link href="/">
              <Image
                src="/krea-distributorforsds.svg"
                alt="Logo"
                width={263}
                height={35}
                className="hidden lg:block"
              />
              <Image
                src="/krea-distributorforsds.svg"
                alt="Logo"
                width={165}
                height={22}
                className="block lg:hidden ml-[5px]"
              />
            </Link>
          </div>
        </div>
      </header>
    );
  }

  return (
    <>
      {/* Fixed Navigation Section */}
      <header className="relative z-50 bg-transparent container-dental">
        <div className="header-logo h-[75px] lg:h-31 flex items-center">
          <div className="sm:ml-[-11px] ml-0">
            <Link href="/">
              <Image
                src="/krea-distributorforsds.svg"
                alt="Logo"
                width={390}
                height={35}
                className="hidden lg:block"
              />
              <Image
                src="/krea-distributorforsds.svg"
                alt="Logo"
                width={265}
                height={22}
                className="block lg:hidden ml-[5px]"
              />
            </Link>
          </div>
        </div>

        <div className="">
          {/* Desktop Navigation Section - Right Half (Fixed) */}
          <div className="duration-30 hidden lg:flex items-center fixed top-0 right-0 bg-white h-31 w-[57.5%] 2xl:w-[55.5%]">
            <div className="container-dental w-full">
              <div className="flex items-center gap-6">
                {/* Navigation Menu */}
                <div className="flex items-center space-x-8">
                  {/* Products */}
                  <div className="relative desktop-menu-item">
                    <button
                      onClick={() => toggleDesktopSubmenu("products")}
                      className={`ml-[10px] flex items-center space-x-1 font-normal text-base transition-colors ${
                        activeDesktopSubmenu === "products"
                          ? "text-primary-500"
                          : "text-primary-600 hover:text-primary-600"
                      }`}
                    >
                      <span className="mr-[5px]">Ürünler</span>
                      <ChevronDownIcon />
                    </button>
                  </div>

                  {/* Service */}
                  <div className="relative desktop-menu-item">
                    <button
                      onClick={() => toggleDesktopSubmenu("service")}
                      className={`flex items-center space-x-1 font-normal text-base transition-colors ${
                        activeDesktopSubmenu === "service"
                          ? "text-primary-500"
                          : "text-primary-600 hover:text-primary-500"
                      }`}
                    >
                      <span className="mr-[5px]">Hizmet</span>
                      <ChevronDownIcon />
                    </button>
                  </div>

                  {/* About SDS */}
                  <div className="relative desktop-menu-item">
                    <button
                      onClick={() => toggleDesktopSubmenu("about")}
                      className={`flex items-center space-x-1 font-normal text-base transition-colors ${
                        activeDesktopSubmenu === "about"
                          ? "text-primary-500"
                          : "text-primary-600 hover:text-primary-600"
                      }`}
                    >
                      <span className="mr-[5px]">SDS Hakkında</span>
                      <ChevronDownIcon />
                    </button>
                  </div>
                </div>
                <div className="flex items-center space-x-8">
                  <Link
                    href="https://education.swissdentalsolutions.com/"
                    className="flex items-center gap-2 px-[20px] py-[13px] border bg-[#f9f9f9] border-[#e0e0e0] text-primary-600 rounded-full hover:bg-primary-600 hover:text-white transition-all duration-300  w-[152px]"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="d-inline-block me-1 me-lg-0 me-xl-1 mt-n1-4 shrink-0"
                    >
                      <path d="M22 9l-10 -4l-10 4l10 4l10 -4v6"></path>
                      <path d="M6 10.6v5.4a6 3 0 0 0 12 0v-5.4"></path>
                    </svg>
                    Eğitim
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Single Desktop Menu Container - Shared for all menus */}
          <div
            className={`fixed top-30 right-0 w-[57.5%] 2xl:w-[55.5%] backdrop-filter backdrop-blur-[35px] bg-[#fafafae6] h-[calc(100vh-124px)] shadow-2xl transition-all duration-300 z-[-1] ${
              activeDesktopSubmenu
                ? "opacity-100 visible translate-y-0"
                : "opacity-0 invisible -translate-y-4"
            }`}
          >
            <div className="p-8 h-full overflow-y-auto">
              {/* Content transition wrapper */}
              <div
                className={`transition-all duration-300 ease-in-out ${
                  isTransitioning
                    ? "opacity-0 transform -translate-y-4"
                    : "opacity-100 transform translate-y-0"
                }`}
              >
                {!isTransitioning && renderMenuContent()}
                {renderLanguageToggle()}
              </div>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden fixed top-0 right-0 z-50 bg-white lg:h-31 h-[75px] flex items-center">
            <div className="px-4">
              <button
                onClick={toggleMobileMenu}
                className="p-2 text-primary-600 hover:text-primary-500 transition-colors mobile-menu-container"
                aria-label={'Menü'}
              >
                {isMobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          <div
            className={`lg:hidden fixed lg:top-31 top-18 left-0 right-0 bg-[#f4f4f4] shadow-2xl transition-all duration-300 ease-in-out z-50 mobile-menu-container ${
              isMobileMenuOpen
                ? "h-[calc(100vh-0px)] opacity-100 visible"
                : "h-0 opacity-0 invisible"
            } overflow-hidden`}
          >
            <div className="relative h-full">
              {/* Main Menu */}
              <div
                className={`absolute inset-0 transition-transform duration-300 ease-in-out ${
                  activeMobileSubmenu ? "-translate-x-full" : "translate-x-0"
                }`}
              >
                <div className="p-6 space-y-6 h-full overflow-y-auto mt-[26px]">
                  {/* Products */}
                  <div className="border-gray-200 mb-[13px]">
                    <button
                      onClick={() => toggleMobileSubmenu("products")}
                      className="flex text-[32px] font-[300] items-center justify-between w-full text-left text-primary-600 hover:text-primary-500 transition-colors text-lg"
                    >
                      <span>Ürünler</span>
                      <ChevronLeftIcon />
                    </button>
                  </div>

                  {/* Service */}
                  <div className="border-gray-200 mb-[13px]">
                    <button
                      onClick={() => toggleMobileSubmenu("service")}
                      className="flex text-[32px] font-[300] items-center justify-between w-full text-left text-primary-600 hover:text-primary-500 transition-colors text-lg"
                    >
                      <span>Hizmet</span>
                      <ChevronLeftIcon />
                    </button>
                  </div>

                  {/* About SDS */}
                  <div className="border-gray-200 mb-[13px]">
                    <button
                      onClick={() => toggleMobileSubmenu("about")}
                      className="flex text-[32px] font-[300] items-center justify-between w-full text-left text-primary-600 hover:text-primary-500 transition-colors text-lg"
                    >
                      <span>SDS Hakkında</span>
                      <ChevronLeftIcon />
                    </button>
                  </div>

                  <div className="flex mt-8 items-center space-x-4">
                  <Link
                    href="https://education.swissdentalsolutions.com/"
                    className="flex items-center gap-2 px-[20px] py-[13px] border bg-[#f9f9f9] border-[#e0e0e0] text-primary-600 rounded-full hover:bg-primary-600 hover:text-white transition-all duration-300  w-[152px]"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="d-inline-block me-1 me-lg-0 me-xl-1 mt-n1-4 shrink-0"
                    >
                      <path d="M22 9l-10 -4l-10 4l10 4l10 -4v6"></path>
                      <path d="M6 10.6v5.4a6 3 0 0 0 12 0v-5.4"></path>
                    </svg>
                    Eğitim
                  </Link>
                  </div>

                  {/* Language Toggle for Mobile */}
                  <div className="mt-8">{renderLanguageToggle()}</div>
                </div>
              </div>

              {/* Submenu */}
              <div
                className={`absolute inset-0 transition-transform duration-300 ease-in-out ${
                  activeMobileSubmenu ? "translate-x-0" : "translate-x-full"
                }`}
              >
                <div className="p-6 h-full overflow-y-auto">
                  {/* Back Button */}
                  <div className="mb-6">
                    <button
                      onClick={() => setActiveMobileSubmenu(null)}
                      className="flex items-center space-x-2 text-primary-600 hover:text-primary-500 transition-colors"
                    >
                      <BackIcon />
                      <span className="text-lg font-medium">
                        Geri
                      </span>
                    </button>
                  </div>

                  {/* Submenu Content */}
                  <div className="space-y-2">
                    {renderMobileSubmenuContent()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;